import React, { useEffect, useState } from 'react';
import { getBotStats } from '../../../../utils/botUtils';

interface BotAnalyticsProps {
    botId: string;
}

export const BotAnalytics: React.FC<BotAnalyticsProps> = ({ botId }) => {
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [exportLoading, setExportLoading] = useState(false);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const data = await getBotStats(botId);
                setStats(data);
            } catch (error) {
                console.error('Error loading analytics:', error);
            } finally {
                setLoading(false);
            }
        };

        if (botId) {
            fetchStats();
        }
    }, [botId]);

    const handleDownload = async () => {
        if (!startDate || !endDate) {
            alert('Please select both start and end dates.');
            return;
        }

        setExportLoading(true);
        try {
            const authLS = sessionStorage.getItem('kt-auth-react-v');
            const auth = authLS ? JSON.parse(authLS) : null;
            const token = auth?.api_token;

            if (!token) {
                alert('Authentication token missing. Please login again.');
                return;
            }

            const APP_API_URL = process.env.REACT_APP_API_URL;

            const response = await fetch(`${APP_API_URL}/export`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ botId, startDate, endDate }),
            });

            if (!response.ok) throw new Error('Export failed');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `analytics_export_${startDate}_to_${endDate}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (error) {
            console.error('Download error:', error);
            alert('Failed to download report.');
        } finally {
            setExportLoading(false);
        }
    };

    if (loading) return <div>Loading statistics...</div>;

    return (
        <div className='card mb-5 mb-xl-5 p-10'>
            <div className='d-flex justify-content-between align-items-center mb-5'>
                <h3 className='fw-bolder m-0'>Performance Analytics</h3>

                <div className='d-flex gap-3 align-items-end'>
                    <div className='d-flex flex-column'>
                        <label className='form-label small text-muted'>Start Date</label>
                        <input
                            type='date'
                            className='form-control form-control-sm'
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                    <div className='d-flex flex-column'>
                        <label className='form-label small text-muted'>End Date</label>
                        <input
                            type='date'
                            className='form-control form-control-sm'
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                    <button
                        className='btn btn-sm btn-primary'
                        onClick={handleDownload}
                        disabled={!startDate || !endDate || exportLoading}
                    >
                        {exportLoading ? (
                            <>
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                <span className='indicator-label'>Exporting...</span>
                            </>
                        ) : (
                            <>
                                <i className='bi bi-download me-1'></i> Export Excel
                            </>
                        )}
                    </button>
                </div>
            </div>

            <div className='row mb-10'>
                {/* <div className='col-md-3'>
                    <div className='card bg-light-primary p-4 text-center'>
                        <div className='fs-2 fw-bolder text-primary'>{stats?.visitors || 0}</div>
                        <div className='text-gray-600 fw-bold'>Total Visitors</div>
                    </div>
                </div> */}
                <div className='col-md-3'>
                    <div className='card bg-light-success p-4 text-center'>
                        <div className='fs-2 fw-bolder text-success'>{stats?.chats || 0}</div>
                        <div className='text-gray-600 fw-bold'>Chats Started</div>
                    </div>
                </div>
                <div className='col-md-3'>
                    <div className='card bg-light-info p-4 text-center'>
                        <div className='fs-2 fw-bolder text-info'>{stats?.leads || 0}</div>
                        <div className='text-gray-600 fw-bold'>Leads Captured</div>
                    </div>
                </div>
                {/* <div className='col-md-3'>
                    <div className='card bg-light-warning p-4 text-center'>
                        <div className='fs-2 fw-bolder text-warning'>{stats?.conversionRate || '0%'}</div>
                        <div className='text-gray-600 fw-bold'>Conversion Rate</div>
                    </div>
                </div> */}
            </div>

            <div className='separator my-10'></div>

            <div className='row'>
                <div className='col-12'>
                    <div className='alert alert-info'>
                        <strong>Note:</strong> "Total Visitors" tracks unique users based on their browser session.
                    </div>
                </div>
            </div>
        </div>
    );
};
